import { AppType } from "@/features/applications";
import { TitleCase } from "@/utils/string";

import { Button, Col, Divider, Form, InputNumber, Modal, Row, Tag, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { oc } from "ts-optchain";
import { round } from "lodash";

import SettingForm from "./SettingForm";
import { alertFormKeys } from "./constants";
import { useAlertUpdate } from "./hooks";
import { useFilterWatchlistData, useWLTabsContext } from "../hooks";
import { useWatchListContext } from "../../../hooks";
import styles from "./styles.less";
import { useRootSelector } from "@/infrastructure/hooks";
import { FireSel, FireType } from "@/features/firestore";

interface IProps {
  setAlertLoading: (e: boolean) => void;
}

export default ({ setAlertLoading }: IProps) => {
  const [form] = Form.useForm();
  const { watchlistData } = useWatchListContext();
  const exchangeCountryMap = useRootSelector(FireSel.exchangeCountryMap)
  const { userAlertLimit, updateAlertLimit } = useAlertUpdate();
  const [margin, setMargin] = useState(10);
  const [selectedLimit, setSeletedLimit] = useState<AppType.AlertLimits[]>([...userAlertLimit]);
  const { alertModal, setAlertModal, selectedStocks, setSelectedStocks } = useWLTabsContext();

  useEffect(() => {
    if (alertModal) {
      setSeletedLimit([...userAlertLimit]);
    }
  }, [alertModal]);

  return (
    <Modal
      title="Set your price limit"
      visible={alertModal}
      onOk={handleSubmit}
      onCancel={() => setAlertModal(false)}
      width={window.innerWidth / 2}
    >
      <Row justify="end" className={styles.autoLimit}>
        <Button type="primary" size="small" className={styles.button} onClick={calSelectedLimitByMargin}>
          Auto Calculate Limit
        </Button>
        <InputNumber
          min={0}
          size="small"
          value={margin}
          formatter={value => `± ${value}%`}
          onChange={value => setMargin(value ? parseFloat(value as string) : 0)}
        />
      </Row>
      {watchlistData
        .filter(e => useFilterWatchlistData(selectedStocks, e.trading_code, e.exchange))
        .map((e, i) => (
          <Row key={i} justify="space-between" className={styles.settingRow}>
            <Col span={24} style={{ marginBottom: 5 }}>
              <Typography.Text underline strong>{`${e.trading_name} [${e.trading_code}]`}</Typography.Text>
            </Col>
            {(["last", "open", "high", "low"] as (keyof FireType.IntradayData)[]).map(a => (
              <Col key={a}>
                <Tag color="geekblue">{TitleCase(a)}:</Tag>
                <span>${e[a]}</span>
              </Col>
            ))}
            <Divider style={{ marginBottom: 5, marginTop: 10 }} />
            <Col span={24}>
              <SettingForm form={form} ticker={e.trading_code} exchange={e.exchange} selectedLimit={selectedLimit} />
            </Col>
          </Row>
        ))}
    </Modal>
  );

  function calSelectedLimitByMargin() {
    const priceData = watchlistData.filter(e => useFilterWatchlistData(selectedStocks, e.trading_code, e.exchange));
    setSeletedLimit(
      priceData.map(e => ({
        ticker: e.trading_code,
        exchange: e.exchange,
        country: exchangeCountryMap[e.exchange],
        isNotify: true,
        upperAlert: round(e.last * (1 + margin / 100), e.last > 1 ? 2 : e.last > 0.1 ? 3 : 4),
        upperWarning: round(e.last * (1 + margin / 100 / 2), e.last > 1 ? 2 : e.last > 0.1 ? 3 : 4),
        lowerAlert: round(e.last * (1 - margin / 100), e.last > 1 ? 2 : e.last > 0.1 ? 3 : 4),
        lowerWarning: round(e.last * (1 - margin / 100 / 2), e.last > 1 ? 2 : e.last > 0.1 ? 3 : 4),
        bidAlert: 0,
        askAlert: 0
      }))
    );
  }

  function handleSubmit() {
    const { getFieldsValue } = form;
    const formValues = getFieldsValue();

    const newLimit = [...userAlertLimit];

    selectedStocks.map(s => {
      const target = newLimit.find(e => e.ticker === s.ticker && e.exchange === s.exchange);
      const upperAlert = oc(formValues[`${alertFormKeys.alertUpper}-${s.ticker}-${s.exchange}`])("");
      const upperWarning = oc(formValues[`${alertFormKeys.warningUpper}-${s.ticker}-${s.exchange}`])("");
      const lowerWarning = oc(formValues[`${alertFormKeys.warningLower}-${s.ticker}-${s.exchange}`])("");
      const lowerAlert = oc(formValues[`${alertFormKeys.alertLower}-${s.ticker}-${s.exchange}`])("");
      const bidAlert = oc(formValues[`${alertFormKeys.bid}-${s.ticker}-${s.exchange}`])("");
      const askAlert = oc(formValues[`${alertFormKeys.ask}-${s.ticker}-${s.exchange}`])("");

      if (target) {
        target.isNotify = true;
        target.upperAlert = upperAlert === "" ? target.upperAlert : upperAlert;
        target.upperWarning = upperWarning === "" ? target.upperWarning : upperWarning;
        target.lowerWarning = lowerWarning === "" ? target.lowerWarning : lowerWarning;
        target.lowerAlert = lowerAlert === "" ? target.lowerAlert : lowerAlert;
        target.bidAlert = bidAlert === "" ? target.bidAlert : bidAlert;
        target.askAlert = askAlert === "" ? target.askAlert : askAlert;
      } else {
        newLimit.push({
          ticker: s.ticker,
          exchange: s.exchange,
          country: exchangeCountryMap[s.exchange],
          isNotify: true,
          upperAlert,
          upperWarning,
          lowerWarning,
          lowerAlert,
          bidAlert,
          askAlert
        });
      }
    });

    updateAlertLimit(newLimit);
    setSelectedStocks([]);

    // Hacky way to force rerender ag-grid frameworkComponents
    // as both refreshCells & redraw do not work
    setAlertLoading(true);
    setTimeout(() => {
      setAlertLoading(false);
    }, 500);

    setAlertModal(false);
  }
};
